import type { ConnectionDraft } from './types.ts';

/**
 * The server's checks on connection settings (`parseConnectionConfig`,
 * `validateEndpointUrl`, `parseSecret`), run in the form so a mistake shows
 * next to its field instead of after a test. The server still checks
 * everything; these only need to agree with it.
 */

export type DraftField =
  | 'endpointUrl'
  | 'secret'
  | 'authHeaderName'
  | 'model'
  | 'sessionResponsePath'
  | 'requestTemplate'
  | 'replyPath';

export type DraftProblems = Partial<Record<DraftField, string>>;

const PLACEHOLDERS = [
  '{{messages}}',
  '{{message}}',
  '{{session_id}}',
  '{{model}}',
];
const HEADER_NAME = /^[A-Za-z0-9-]{1,100}$/;
const RESERVED_HEADERS = new Set([
  'content-type',
  'content-length',
  'host',
  'accept',
  'user-agent',
  'x-promptsafe-request-id',
]);
const SECRET = /^[\x20-\x7e]+$/;
const MAX_SECRET_LENGTH = 4096;
const PATH_SEGMENT = /^[A-Za-z0-9_-]{1,100}$/;
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);
/** The server's limits on each setting, after trimming. */
const MAX_LENGTH = {
  endpointUrl: 2000,
  model: 200,
  path: 200,
  requestTemplate: 10_000,
};
const TOO_LONG = 'This is too long.';
/** Vite's dev flag. Undefined outside Vite, e.g. in the Deno tests. */
const IS_DEV = Boolean(
  (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV,
);

/** Problems that stop the settings from being tested, by field. */
export function findDraftErrors(
  draft: ConnectionDraft,
  secret: string,
  hasSavedSecret: boolean,
): DraftProblems {
  const errors: DraftProblems = {};
  const isJson = draft.format === 'json';

  const url = urlProblem(draft.endpointUrl, isJson);
  if (url) errors.endpointUrl = url;

  const trimmedSecret = secret.trim();
  if (!trimmedSecret) {
    if (!hasSavedSecret) {
      errors.secret = isJson ? 'Enter the secret.' : 'Enter the API key.';
    }
  } else if (trimmedSecret.length > MAX_SECRET_LENGTH) {
    errors.secret = TOO_LONG;
  } else if (!SECRET.test(trimmedSecret)) {
    errors.secret =
      'It can only contain letters, numbers and punctuation, on one line.';
  }

  if (!isJson) {
    const model = draft.model.trim();
    if (!model) {
      errors.model = 'Enter the model name your server expects.';
    } else if (model.length > MAX_LENGTH.model) {
      errors.model = TOO_LONG;
    }
    return errors;
  }

  if (draft.authType === 'header') {
    const name = draft.authHeaderName.trim();
    if (!HEADER_NAME.test(name)) {
      errors.authHeaderName =
        'Enter a header name like "X-API-Key": letters, numbers and dashes.';
    } else if (RESERVED_HEADERS.has(name.toLowerCase())) {
      errors.authHeaderName = `"${name}" is set by PromptSafe. Choose another header name.`;
    }
  }

  if (
    draft.conversationMode === 'session' &&
    draft.sessionIdSource === 'response'
  ) {
    const path = draft.sessionResponsePath.trim();
    const problem = path
      ? pathProblem(path)
      : 'Enter where your endpoint puts the session ID in its reply.';
    if (problem) errors.sessionResponsePath = problem;
  }

  const template = templateProblem(draft.requestTemplate);
  if (template) errors.requestTemplate = template;

  const replyPath = draft.replyPath.trim();
  if (replyPath) {
    const problem = pathProblem(replyPath);
    if (problem) errors.replyPath = problem;
  }
  return errors;
}

/**
 * Settings that work but probably aren't what was meant. Shown, never
 * blocking: the endpoint may know better.
 */
export function findDraftWarnings(draft: ConnectionDraft): DraftProblems {
  if (draft.format !== 'json') return {};
  const used = placeholdersIn(draft.requestTemplate);
  if (!used) return {};
  if (draft.conversationMode === 'session' && !used.has('{{session_id}}')) {
    return {
      requestTemplate:
        'There\'s no "{{session_id}}", so your endpoint can\'t tell simulations apart or remember earlier messages.',
    };
  }
  if (draft.conversationMode === 'stateless' && !used.has('{{messages}}')) {
    return {
      requestTemplate:
        'There\'s no "{{messages}}", so your endpoint only sees the latest message, not the conversation so far.',
    };
  }
  return {};
}

function urlProblem(raw: string, isJson: boolean): string | null {
  const value = raw.trim();
  if (!value) return isJson ? 'Enter the endpoint URL.' : 'Enter the base URL.';
  if (value.length > MAX_LENGTH.endpointUrl) return TOO_LONG;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return isJson
      ? 'Enter a full URL, like "https://bot.example.com/chat".'
      : 'Enter a full URL, like "https://llm.example.com/v1".';
  }
  // The local stack can call a mock endpoint over plain HTTP.
  const httpAllowed = url.protocol === 'http:' && IS_DEV;
  if (url.protocol !== 'https:' && !httpAllowed) {
    return 'The URL must start with https://.';
  }
  if (url.username || url.password) {
    return "Don't put credentials in the URL. Use the secret field instead.";
  }
  return null;
}

function pathProblem(path: string): string | null {
  if (path.length > MAX_LENGTH.path) return TOO_LONG;
  const segments = path.split('.');
  const valid =
    segments.length <= 20 &&
    segments.every(
      (segment) =>
        PATH_SEGMENT.test(segment) && !FORBIDDEN_SEGMENTS.has(segment),
    );
  return valid
    ? null
    : 'Use names and numbers separated by dots, like "data.0.text".';
}

function templateProblem(raw: string): string | null {
  const template = raw.trim();
  // Blank uses the default template.
  if (!template) return null;
  if (template.length > MAX_LENGTH.requestTemplate) return TOO_LONG;
  let parsed: unknown;
  try {
    parsed = JSON.parse(template);
  } catch (error) {
    const reason = error instanceof SyntaxError ? ` ${error.message}.` : '';
    return `This isn't valid JSON.${reason} Check for a missing quote, comma or brace.`;
  }
  if (!parsed || typeof parsed !== 'object') {
    return 'The template must be a JSON object, like { "message": "{{message}}" }.';
  }
  const found = findPartialPlaceholder(parsed);
  if (found) return describePlaceholderProblem(found);
  const used = new Set<string>();
  collectPlaceholders(parsed, used);
  if (!used.has('{{message}}') && !used.has('{{messages}}')) {
    return 'Include "{{message}}" or "{{messages}}", or your endpoint never receives the conversation.';
  }
  return null;
}

/**
 * The placeholders a valid custom template uses, read from its parsed values
 * so escapes like \u007b count, or null when there's no usable template.
 */
function placeholdersIn(raw: string): Set<string> | null {
  const template = raw.trim();
  if (!template) return null;
  try {
    const used = new Set<string>();
    collectPlaceholders(JSON.parse(template), used);
    return used;
  } catch {
    return null;
  }
}

function collectPlaceholders(value: unknown, used: Set<string>) {
  if (typeof value === 'string') {
    if (PLACEHOLDERS.includes(value)) used.add(value);
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectPlaceholders(item, used));
  }
}

/** Same as the server's `findPartialPlaceholder`. */
function findPartialPlaceholder(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.includes('{{') && !PLACEHOLDERS.includes(value) ? value : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findPartialPlaceholder(item);
      if (found) return found;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value)) {
      if (key.includes('{{')) return key;
      const found = findPartialPlaceholder(item);
      if (found) return found;
    }
  }
  return null;
}

/** Same wording as the server's `describePlaceholderProblem`. */
function describePlaceholderProblem(found: string): string {
  if (/^\{\{[^{}]*\}\}$/.test(found.trim())) {
    return `"${found}" isn't a placeholder. Use one of ${PLACEHOLDERS.map((p) => `"${p}"`).join(', ')}.`;
  }
  return `Placeholders must be the whole value, like "message": "{{message}}". "${found}" has one inside other text.`;
}
